import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { useAuthStore } from '../../../stores/authStore';
import { AxiosError } from 'axios';

export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
}

export interface AuthResponse {
  token: string;
  refreshToken?: string;
  user: {
    id: string;
    email: string;
    name: string;
  };
}

export interface UserResponse {
  id: string;
  email: string;
  name: string;
  locale?: string;
  timezone?: string;
  currency?: string;
  dateFormat?: string;
  numberFormat?: string;
  tier?: string;
  createdAt: string;
  updatedAt: string;
}

// Backend response format (snake_case)
interface BackendAuthResponse {
  access_token: string;
  refresh_token: string;
  user: {
    id: string;
    email: string;
    name: string;
  };
}

// API wrapper response format
interface ApiWrappedResponse<T> {
  success: boolean;
  data: T;
  meta?: {
    timestamp: string;
  };
}

// Transform backend response to frontend format
function transformAuthResponse(backendResponse: BackendAuthResponse): AuthResponse {
  return {
    token: backendResponse.access_token,
    refreshToken: backendResponse.refresh_token,
    user: backendResponse.user,
  };
}

export interface GoogleAuthData {
  idToken: string;
}

interface GoogleAuthResult {
  link_required?: boolean;
  email?: string;
  name?: string;
  auth?: AuthResponse;
}

// API functions
async function googleAuthApi(data: GoogleAuthData): Promise<GoogleAuthResult> {
  const response = await apiClient.post<ApiWrappedResponse<BackendAuthResponse & { link_required?: boolean; email?: string; name?: string }>>('/api/auth/google', data);
  const raw = response.data.data;
  if (raw.link_required) {
    return { link_required: true, email: raw.email, name: raw.name };
  }
  return { auth: transformAuthResponse(raw) };
}

async function linkGoogleAccountApi(data: GoogleAuthData): Promise<AuthResponse> {
  const response = await apiClient.post<ApiWrappedResponse<BackendAuthResponse>>('/api/auth/google/link', data);
  return transformAuthResponse(response.data.data);
}

async function loginApi(credentials: LoginCredentials): Promise<AuthResponse> {
  const { rememberMe, ...loginData } = credentials;
  const response = await apiClient.post<ApiWrappedResponse<BackendAuthResponse>>('/api/auth/login', loginData);
  // Unwrap the API response wrapper
  return transformAuthResponse(response.data.data);
}

async function registerApi(data: RegisterData): Promise<AuthResponse> {
  const response = await apiClient.post<ApiWrappedResponse<BackendAuthResponse>>('/api/auth/register', data);
  // Unwrap the API response wrapper
  return transformAuthResponse(response.data.data);
}

async function logoutApi(): Promise<void> {
  try {
    await apiClient.post('/api/auth/logout');
  } catch (error) {
    // Ignore logout errors - we'll clear local state anyway
    console.warn('Logout API call failed:', error);
  }
}

async function getCurrentUserApi(): Promise<UserResponse> {
  const response = await apiClient.get<ApiWrappedResponse<UserResponse>>('/api/auth/me');
  // Unwrap the API response wrapper
  return response.data.data;
}

// React Query hooks
export function useLogin() {
  const { login: loginStore } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation<AuthResponse, AxiosError, LoginCredentials>({
    mutationFn: loginApi,
    onSuccess: (data, variables) => {
      const rememberMe = variables.rememberMe || false;
      loginStore(
        data.token,
        data.refreshToken || '',
        data.user,
        rememberMe
      );
      queryClient.invalidateQueries();
    },
  });
}

export function useRegister() {
  return useMutation<AuthResponse, AxiosError, RegisterData>({
    mutationFn: registerApi,
  });
}

export function useLogout() {
  const { logout: logoutStore } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, void>({
    mutationFn: logoutApi,
    onSuccess: () => {
      logoutStore();
      queryClient.clear();
    },
    onError: () => {
      // Even if the API call fails, clear local state
      logoutStore();
      queryClient.clear();
    },
  });
}

export function useGoogleAuth() {
  const { login: loginStore } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation<GoogleAuthResult, AxiosError, GoogleAuthData>({
    mutationFn: googleAuthApi,
    onSuccess: (data) => {
      if (data.auth) {
        loginStore(
          data.auth.token,
          data.auth.refreshToken || '',
          data.auth.user,
          true // Google auth always remembers
        );
        queryClient.invalidateQueries();
      }
      // If link_required, the component handles showing the confirmation dialog
    },
  });
}

export function useLinkGoogleAccount() {
  const { login: loginStore } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation<AuthResponse, AxiosError, GoogleAuthData>({
    mutationFn: linkGoogleAccountApi,
    onSuccess: (data) => {
      loginStore(
        data.token,
        data.refreshToken || '',
        data.user,
        true
      );
      queryClient.invalidateQueries();
    },
  });
}

export function useCurrentUser() {
  const { isAuthenticated } = useAuthStore();

  return useQuery<UserResponse, AxiosError>({
    queryKey: ['currentUser'],
    queryFn: getCurrentUserApi,
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
